"use client";

import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const DATE_MIN = "1900-01-01";

/** Locale-to-format mapping for strict parsing. US uses MM/dd/yyyy, German uses dd.MM.yyyy. */
function getLocaleFormat(locale: string): { format: string; separator: string } {
  const lang = locale.split("-")[0];
  if (locale.startsWith("de") || lang === "de") {
    return { format: "dd.MM.yyyy", separator: "." };
  }
  return { format: "MM/dd/yyyy", separator: "/" };
}

export interface DatePickerFieldProps
  extends Omit<
    React.ComponentProps<typeof Input>,
    "type" | "value" | "onChange" | "min" | "max"
  > {
  /** Current value as Date or undefined */
  value?: Date;
  /** Called when user selects a date (undefined when cleared) */
  onChange?: (date: Date | undefined) => void;
  /** Max selectable date (default: today) */
  maxDate?: Date;
  /** Min selectable date (default: 1900-01-01) */
  minDate?: Date;
  /** Placeholder when no date selected */
  placeholder?: string;
  /** BCP 47 locale (e.g. "en-US", "de-DE", "ja-JP"). Defaults to navigator.language so all locales are supported. */
  locale?: string;
}

/**
 * Date picker using a controlled text input with locale-aware masking and strict parsing.
 * Avoids the MM/DD vs DD/MM ambiguity: US users get MM/dd/yyyy, German users get dd.MM.yyyy.
 * Only parses when the input string is complete; prevents jumping/crashing when typing day > 12 in US locale.
 */
const DatePickerField = React.forwardRef<HTMLInputElement, DatePickerFieldProps>(
  (
    {
      className,
      value,
      onChange,
      maxDate = new Date(),
      minDate = new Date(DATE_MIN),
      placeholder = "Pick a date",
      disabled,
      locale: localeProp,
      ...props
    },
    ref
  ) => {
    const locale =
      localeProp ??
      (typeof navigator !== "undefined" ? navigator.language : "en-US");
    const { format: formatStr, separator } = getLocaleFormat(locale);

    const [localValue, setLocalValue] = React.useState<string | null>(null);
    const [error, setError] = React.useState("");

    const valueStr = value ? format(value, formatStr) : "";

    React.useEffect(() => {
      setLocalValue(null);
      setError("");
    }, [valueStr]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let raw = e.target.value.replace(/\D/g, "");

      if (raw.length > 8) raw = raw.substring(0, 8);

      let masked = raw;
      if (raw.length > 2) {
        masked = raw.substring(0, 2) + separator + raw.substring(2);
      }
      if (raw.length > 4) {
        masked =
          raw.substring(0, 2) +
          separator +
          raw.substring(2, 4) +
          separator +
          raw.substring(4);
      }

      setLocalValue(masked);
      setError("");

      if (masked.length === 10) {
        try {
          const parsed = parse(masked, formatStr, new Date());
          if (isValid(parsed)) {
            if (parsed.getTime() > maxDate.getTime()) {
              setError("Date cannot be in the future");
              onChange?.(undefined);
            } else if (parsed.getTime() < minDate.getTime()) {
              setError("Date is too far in the past");
              onChange?.(undefined);
            } else {
              onChange?.(parsed);
            }
          } else {
            setError(`Invalid date. Use ${formatStr}`);
            onChange?.(undefined);
          }
        } catch {
          setError(`Invalid date. Use ${formatStr}`);
          onChange?.(undefined);
        }
      } else if (masked.length === 0) {
        onChange?.(undefined);
      } else {
        onChange?.(undefined);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const current = localValue ?? valueStr;
      if (current && current.length < 10) {
        setError(`Complete the date (${formatStr})`);
      }
      props.onBlur?.(e);
    };

    const displayValue = localValue ?? valueStr;
    const placeholderText =
      formatStr === "dd.MM.yyyy" ? "dd.mm.yyyy" : "mm/dd/yyyy";

    return (
      <div className="flex flex-col">
        <Input
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="bday"
          placeholder={placeholderText}
          value={displayValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={disabled}
          className={cn("h-12", error && "border-destructive", className)}
          aria-label={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? `${props.id}-error` : undefined}
          {...props}
        />
        {error && (
          <span
            id={props.id ? `${props.id}-error` : undefined}
            className="text-destructive text-sm mt-1"
          >
            {error}
          </span>
        )}
      </div>
    );
  }
);
DatePickerField.displayName = "DatePickerField";

export { DatePickerField };
